/**
 * Sumber tunggal format & parsing tanggal id-ID.
 *
 * KENAPA BERKAS INI ADA: sebelum ini tidak ada helper tanggal sama sekali di
 * `lib/` — `toLocaleDateString` dipakai tersebar di banyak berkas dengan opsi
 * berbeda-beda, dan default "hari ini" di banyak halaman memakai
 * `new Date().toISOString().slice(0, 10)`.
 *
 * JEBAKAN YANG DIHINDARI: `toISOString()` SELALU mengembalikan tanggal UTC.
 * Untuk WIB (UTC+7), setiap hari sebelum jam 07:00 pagi, tanggal UTC MASIH
 * tanggal KEMARIN. Contoh nyata: jam 02:00 WIB tanggal 10 Agustus 2026 =
 * 19:00 UTC tanggal 9 Agustus 2026 — `toISOString().slice(0,10)` akan
 * mengembalikan "2026-08-09", SALAH SEHARI. Ini sudah menjadi bug nyata di
 * beberapa halaman (default filter tanggal/periode). Semua helper "hari ini"
 * di bawah membaca jam melalui `Intl.DateTimeFormat` dengan `timeZone: "Asia/Jakarta"`
 * secara eksplisit, jadi hasilnya benar apa pun timezone perangkat pengguna
 * (laptop di-setting UTC, server di luar negeri, dst).
 *
 * KONTRAK FORMAT ISO: backend menyimpan field tanggal bisnis (mis. `tanggal`
 * transaksi/nota/jurnal) sebagai string tanggal MURNI `"YYYY-MM-DD"` (lihat
 * `_normalisasi_tanggal` di `backend/routers/acquisitions.py` — memakai
 * `datetime.strptime(t, "%Y-%m-%d").date().isoformat()`, BUKAN jam/timezone).
 * String tanggal murni ini TIDAK PERNAH diproses lewat konversi timezone di
 * sini — itu cuma tiga angka kalender, bukan instan waktu. Field timestamp
 * penuh (mis. `created_at` dari `deps.py`, `datetime.now(timezone.utc).isoformat()`)
 * ADALAH instan waktu sungguhan dan dikonversi eksplisit ke WIB sebelum
 * ditampilkan.
 */

const ZONA_WAKTU = "Asia/Jakarta";

const NAMA_BULAN_SINGKAT = [
  "Jan", "Feb", "Mar", "Apr", "Mei", "Jun",
  "Jul", "Agu", "Sep", "Okt", "Nov", "Des",
];

const RE_TANGGAL_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const RE_BULAN_ISO = /^(\d{4})-(\d{2})$/;
const RE_TANGGAL_ID = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const RE_BULAN_ID = /^(\d{1,2})\/(\d{4})$/;

/** Ambil bagian tanggal/jam "sekarang" di WIB, apa pun timezone perangkat. */
function bagianWaktuJakarta(tanggal = new Date()) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: ZONA_WAKTU,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  const bagian = Object.fromEntries(fmt.formatToParts(tanggal).map((p) => [p.type, p.value]));
  return bagian; // { year, month, day, hour, minute, second }
}

/** Tanggal hari ini di WIB, format "YYYY-MM-DD" — pengganti aman untuk
 *  `new Date().toISOString().slice(0,10)` yang salah sebelum jam 07:00 WIB. */
export function hariIniISO() {
  const { year, month, day } = bagianWaktuJakarta();
  return `${year}-${month}-${day}`;
}

/** Bulan berjalan di WIB, format "YYYY-MM" (untuk default filter periode/MonthPicker). */
export function bulanIniISO() {
  const { year, month } = bagianWaktuJakarta();
  return `${year}-${month}`;
}

/** Timestamp lokal WIB "YYYY-MM-DDTHH:mm:ss" (naive, tanpa offset) — dipakai
 *  saat form butuh nilai "sekarang" yang dibaca manusia, bukan dikirim balik
 *  sebagai instan UTC (untuk itu biarkan backend yang mencatat `created_at`). */
export function sekarangISO() {
  const { year, month, day, hour, minute, second } = bagianWaktuJakarta();
  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
}

/** Jam:menit sekarang di WIB, format "HH:mm". */
export function jamSekarangJakarta() {
  const { hour, minute } = bagianWaktuJakarta();
  return `${hour}:${minute}`;
}

/** Objek Date yang merepresentasikan "hari ini" di WIB (jam 00:00 lokal browser). */
export function hariIniDate() {
  return dateDariIso(hariIniISO());
}

/**
 * Format tampilan dd/mm/yyyy dari ISO. Menerima DUA bentuk:
 *  - Tanggal murni "YYYY-MM-DD" -> diformat langsung dari digit, TANPA lewat
 *    objek Date (menghindari geseran timezone untuk tanggal kalender polos).
 *  - Timestamp lengkap (punya "T" / offset) -> instan waktu sungguhan,
 *    dikonversi eksplisit ke WIB baru diformat.
 *
 * @param {string} iso
 * @param {{denganJam?: boolean}} opsi - true untuk varian "dd/mm/yyyy HH:mm"
 */
export function formatTanggal(iso, opsi = {}) {
  const { denganJam = false } = opsi;
  if (!iso) return "-";
  const hanyaTanggal = RE_TANGGAL_ISO.exec(iso);
  if (hanyaTanggal) {
    const [, y, m, d] = hanyaTanggal;
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "-";
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: ZONA_WAKTU,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const bagian = Object.fromEntries(fmt.formatToParts(dt).map((p) => [p.type, p.value]));
  const dasar = `${bagian.day}/${bagian.month}/${bagian.year}`;
  return denganJam ? `${dasar} ${bagian.hour}:${bagian.minute}` : dasar;
}

/** Format panjang ala "10 Agustus 2026" (opsional dengan nama hari). Menerima
 *  tanggal murni maupun timestamp lengkap, dengan aturan konversi sama seperti
 *  `formatTanggal`. */
export function formatTanggalPanjang(iso, opsi = {}) {
  const { denganHari = false } = opsi;
  if (!iso) return "-";
  const opsiFormat = { day: "numeric", month: "long", year: "numeric" };
  if (denganHari) opsiFormat.weekday = "long";
  if (RE_TANGGAL_ISO.test(iso)) {
    const d = dateDariIso(iso);
    return d ? d.toLocaleDateString("id-ID", opsiFormat) : "-";
  }
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "-";
  return dt.toLocaleDateString("id-ID", { ...opsiFormat, timeZone: ZONA_WAKTU });
}

/** Jam:menit WIB dari timestamp lengkap (mis. `created_at`), format "HH:mm".
 *  Sengaja TIDAK memakai `Intl.DateTimeFormat("id-ID", ...).format()` — locale
 *  id-ID pada beberapa runtime ICU memisah jam dengan titik ("02.30"), bukan
 *  titik dua, beda dengan konvensi "HH:mm" yang dipakai input `type="time"`
 *  di seluruh app. Bagian jam/menit diambil manual lalu digabung sendiri. */
export function formatJam(iso) {
  if (!iso) return "-";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "-";
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: ZONA_WAKTU, hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const bagian = Object.fromEntries(fmt.formatToParts(dt).map((p) => [p.type, p.value]));
  return `${bagian.hour}:${bagian.minute}`;
}

/** "YYYY-MM" -> "Agustus 2026" (untuk judul/header periode). */
export function formatBulan(isoBulan) {
  const m = RE_BULAN_ISO.exec(isoBulan || "");
  if (!m) return "-";
  const [, y, bln] = m;
  const d = new Date(Number(y), Number(bln) - 1, 1);
  return d.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
}

/** "YYYY-MM" -> "08/2026" — representasi ringkas yang dipakai sebagai teks
 *  ketikan di <MonthPicker> (urutan bulan/tahun konsisten dengan dd/mm/yyyy). */
export function formatBulanSingkat(isoBulan) {
  const m = RE_BULAN_ISO.exec(isoBulan || "");
  if (!m) return "";
  return `${m[2]}/${m[1]}`;
}

/** Parse ketikan "dd/mm/yyyy" -> ISO "YYYY-MM-DD", atau `null` kalau tidak
 *  valid (termasuk tanggal yang tidak ada, mis. "31/02/2026"). */
export function parseTanggalID(teks) {
  const m = RE_TANGGAL_ID.exec(String(teks || "").trim());
  if (!m) return null;
  const d = Number(m[1]), bln = Number(m[2]), y = Number(m[3]);
  if (bln < 1 || bln > 12 || d < 1 || d > 31) return null;
  // Validasi tanggal betulan lewat round-trip objek Date (menolak 31/02, dst).
  const cek = new Date(y, bln - 1, d);
  if (cek.getFullYear() !== y || cek.getMonth() !== bln - 1 || cek.getDate() !== d) return null;
  return `${String(y).padStart(4, "0")}-${String(bln).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Parse ketikan "mm/yyyy" -> ISO "YYYY-MM", atau `null` kalau tidak valid. */
export function parseBulanID(teks) {
  const m = RE_BULAN_ID.exec(String(teks || "").trim());
  if (!m) return null;
  const bln = Number(m[1]), y = Number(m[2]);
  if (bln < 1 || bln > 12) return null;
  return `${String(y).padStart(4, "0")}-${String(bln).padStart(2, "0")}`;
}

/** ISO tanggal murni "YYYY-MM-DD" -> objek Date lokal (jam 00:00 di timezone
 *  BROWSER, bukan UTC). Dibuat lewat konstruktor (year, month, day) yang
 *  SELALU lokal — sengaja TIDAK memakai `new Date("YYYY-MM-DD")` karena itu
 *  di-parse sebagai UTC midnight dan bisa tampil mundur satu hari di
 *  timezone negatif. Dipakai untuk `selected`/`onSelect` react-day-picker. */
export function dateDariIso(iso) {
  const m = RE_TANGGAL_ISO.exec(iso || "");
  if (!m) return undefined;
  const [, y, bln, d] = m;
  return new Date(Number(y), Number(bln) - 1, Number(d));
}

/** Kebalikan `dateDariIso`: objek Date (dianggap tanggal kalender lokal,
 *  seperti hasil pilihan react-day-picker) -> ISO "YYYY-MM-DD". */
export function isoDariDate(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export { NAMA_BULAN_SINGKAT, ZONA_WAKTU };
